import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdResult } from '../../../mocks/adResults';
import { getSupabaseFunctionUrl, supabase } from '../../../lib/supabase';
import VideoAdModal from './VideoAdModal';
import { VIDEO_AD_GENERATE_CREDITS } from '../../../config/videoAd';
import { downloadFile, captureNodeAsPng } from '../../../lib/download';
import CopyResultsSection, { type BusinessContext, type CopyType } from './CopyResultsSection';
import { getImageStyleConfig } from '../../../config/imageStyles';
import { getEdgeFunctionHeaders } from '../../../lib/edgeFunctions';

const EDIT_AD_IMAGE_URL = getSupabaseFunctionUrl('edit-ad-image');
import { useCreditsContext } from '../../../context/CreditsContext';
import type { EditEntry } from '../../../hooks/useAdSession';
import { EDIT_MODES, getEditModeConfig, type EditModeId } from '../../../config/imageEngines';

/* ─── Copy Item ─────────────────────────────────────────────────────────── */
function CopyItem({ content, index }: { content: string; index: number }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="group flex items-start gap-3 bg-white rounded-xl border border-stone-100 p-4 hover:border-orange-200 hover:shadow-sm transition-all duration-200">
      <span className="shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-stone-100 text-stone-400 text-xs font-bold group-hover:bg-orange-500 group-hover:text-white transition-colors">
        {index + 1}
      </span>
      <p className="flex-1 text-sm text-stone-700 leading-relaxed">{content}</p>
      <button
        onClick={handleCopy}
        className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
          copied ? 'bg-green-500 border-green-500 text-white' : 'bg-stone-50 border-stone-200 text-stone-400 hover:border-orange-400 hover:text-orange-500'
        }`}
        title="Copy to clipboard"
      >
        <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
      </button>
    </div>
  );
}

/* ─── Result Section ─────────────────────────────────────────────────────── */
function ResultSection({ icon, title, badge, items, accentBg, accentText }: {
  icon: string; title: string; badge?: string; items: string[]; accentBg: string; accentText: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <div className={`w-6 h-6 flex items-center justify-center rounded-md ${accentBg}`}>
          <i className={`${icon} ${accentText} text-xs`} />
        </div>
        <h3 className="text-xs font-bold text-stone-800 uppercase tracking-widest">{title}</h3>
        {badge && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${accentBg} ${accentText}`}>{badge}</span>
        )}
        <span className="text-xs text-stone-400 ml-auto">{items.length} variations</span>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((item, idx) => <CopyItem key={idx} content={item} index={idx} />)}
      </div>
    </div>
  );
}

/* ─── Creative Direction ─────────────────────────────────────────────────── */
function CreativeDirection({ prompt, label }: { prompt: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="rounded-xl border border-stone-100 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-md bg-stone-200 text-stone-500">
            <i className="ri-lightbulb-line text-xs" />
          </div>
          <span className="text-xs font-bold text-stone-600 uppercase tracking-widest">Creative Direction</span>
          {label && <span className="text-xs text-stone-400 font-medium">{label}</span>}
        </div>
        <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-stone-400 text-lg`} />
      </button>
      {open && (
        <div className="px-4 py-4 bg-white border-t border-stone-100">
          <div className="relative">
            <p className="text-xs text-stone-500 leading-relaxed font-mono bg-stone-50 rounded-lg p-3 pr-10 border border-stone-100 whitespace-pre-wrap">{prompt}</p>
            <button
              onClick={handleCopy}
              className={`absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
                copied ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-stone-200 text-stone-400 hover:border-orange-400 hover:text-orange-500'
              }`}
            >
              <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
            </button>
          </div>
          <p className="text-xs text-stone-400 mt-2 flex items-center gap-1.5">
            <i className="ri-information-line text-stone-300" />
            This prompt was sent to the GPT Image model to generate the creative image.
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Style label resolver — parses real style name from image prompt ─────── */
const STYLE_INFO_MAP: Array<{ keyword: string; label: string; desc: string; icon: string }> = [
  { keyword: 'Hyper-Realistic',         label: 'Hyper-Realistic',    desc: 'DSLR · Natural light',     icon: 'ri-camera-lens-line' },
  { keyword: 'Cinematic Moody',          label: 'Cinematic',          desc: 'Film · High contrast',     icon: 'ri-movie-line' },
  { keyword: 'Flat Vector',              label: 'Flat Illustration',  desc: 'Vector · Clean',           icon: 'ri-shapes-line' },
  { keyword: '3D Clay',                  label: '3D Clay Render',     desc: 'Polished CGI',             icon: 'ri-box-3-line' },
  { keyword: 'Blueprint',               label: 'Blueprint',          desc: 'Technical drawing',        icon: 'ri-compasses-line' },
  { keyword: 'Bold Graphic Poster',      label: 'Bold Poster',        desc: 'High contrast · Punchy',   icon: 'ri-layout-masonry-line' },
  { keyword: 'Blue-Collar Authentic',   label: 'Blue-Collar',        desc: 'Raw · Authentic',          icon: 'ri-tools-line' },
  { keyword: 'UGC / iPhone',            label: 'UGC / iPhone',       desc: 'Shot on iPhone · Real',    icon: 'ri-smartphone-line' },
  { keyword: 'Before / After',          label: 'Before / After',     desc: 'Problem → Solution',       icon: 'ri-contrast-2-line' },
  { keyword: 'Problem-Focused',         label: 'Pain Point',         desc: 'Problem hook · Emotional', icon: 'ri-emotion-unhappy-line' },
  { keyword: 'Testimonial',             label: 'Testimonial',        desc: 'Social proof · Trust',     icon: 'ri-user-heart-line' },
  { keyword: 'Retro / Vintage',         label: 'Retro / Vintage',    desc: 'Film grain · Nostalgic',   icon: 'ri-film-line' },
  { keyword: 'Luxury / High-End',       label: 'Luxury',             desc: 'Premium · Minimal',        icon: 'ri-vip-crown-line' },
  { keyword: 'Comic Book',              label: 'Comic / Cartoon',    desc: 'Bold outlines · Fun',      icon: 'ri-pen-nib-line' },
  { keyword: 'Infographic',             label: 'Infographic',        desc: 'Structured · Clear',       icon: 'ri-bar-chart-2-line' },
  { keyword: 'Grunge / Rugged',         label: 'Grunge / Rugged',    desc: 'Heavy texture · Gritty',   icon: 'ri-thunderstorms-line' },
  { keyword: 'Neon / Futuristic',       label: 'Neon / Futuristic',  desc: 'Cyberpunk · Glowing',      icon: 'ri-lightbulb-flash-line' },
  { keyword: 'Pattern Interrupt',       label: 'Pattern Interrupt',  desc: 'Unexpected · Scroll-stop', icon: 'ri-question-line' },
  { keyword: 'Minimal Object',          label: 'Minimal Object',     desc: 'One object · Clean',       icon: 'ri-focus-3-line' },
  { keyword: 'Text-First',              label: 'Text-First',         desc: 'Copy-first layout',        icon: 'ri-font-size' },
];

// Fallback labels for when no prompt or style ID is available
const FALLBACK_LABELS: Record<number, { label: string; desc: string; icon: string }> = {
  0: { label: 'Creative A', desc: 'AI auto style', icon: 'ri-magic-line' },
  1: { label: 'Creative B', desc: 'AI auto style', icon: 'ri-magic-line' },
  2: { label: 'Creative C', desc: 'AI auto style', icon: 'ri-magic-line' },
  3: { label: 'Creative D', desc: 'AI auto style', icon: 'ri-magic-line' },
  4: { label: 'Creative E', desc: 'AI auto style', icon: 'ri-magic-line' },
  5: { label: 'Creative F', desc: 'AI auto style', icon: 'ri-magic-line' },
  6: { label: 'Creative G', desc: 'AI auto style', icon: 'ri-magic-line' },
};

const categoryIconMap: Record<string, string> = {
  photography: 'ri-camera-lens-line',
  marketing: 'ri-megaphone-line',
  illustration: 'ri-pen-nib-line',
  creative: 'ri-sparkling-line',
};

function getStyleInfoFromId(styleId: string): { label: string; desc: string; icon: string } | null {
  const config = getImageStyleConfig(styleId);
  if (!config) return null;
  return {
    label: config.label,
    desc: config.description,
    icon: categoryIconMap[config.category] ?? 'ri-image-line',
  };
}

const STYLE_PROMPT_MATCHERS: Array<{ styleId: string; patterns: string[] }> = [
  { styleId: 'style-hyperrealistic', patterns: ['hyper-realistic photography', 'full-frame professional dslr'] },
  { styleId: 'style-cinematic', patterns: ['cinematic moody', 'cinematic photography', 'dramatic film-quality'] },
  { styleId: 'style-ugc', patterns: ['ugc / iphone', 'user-generated content', 'shot on iphone'] },
  { styleId: 'style-bluecollar', patterns: ['blue-collar authentic', 'skilled tradesperson', 'blue collar'] },
  { styleId: 'style-luxury', patterns: ['luxury / high-end', 'luxury high-end', 'premium photography'] },
  { styleId: 'style-bold-poster', patterns: ['bold graphic poster', 'bold high impact graphic', 'strong typography'] },
  { styleId: 'style-before-after', patterns: ['before / after', 'before and after', 'transformation split-screen'] },
  { styleId: 'style-testimonial', patterns: ['testimonial', 'customer review', 'social proof'] },
  { styleId: 'style-pain-point', patterns: ['problem-focused', 'pain point', 'problem awareness'] },
  { styleId: 'style-infographic', patterns: ['infographic / educational', 'data-visual', 'educational visual'] },
  { styleId: 'style-process-step-in-action', patterns: ['process / step-in-action', 'mid-action process'] },
  { styleId: 'style-clay', patterns: ['3d clay', 'clay render', 'pixar-style clay'] },
  { styleId: 'style-flat', patterns: ['flat vector', 'flat illustration', 'vector illustration'] },
  { styleId: 'style-comic', patterns: ['comic book', 'cartoon illustration', 'pop art'] },
  { styleId: 'style-blueprint', patterns: ['blueprint', 'technical engineering'] },
  { styleId: 'style-neon', patterns: ['neon / futuristic', 'cyberpunk', 'neon glow'] },
  { styleId: 'style-retro', patterns: ['retro / vintage', 'vintage advertisement'] },
  { styleId: 'style-grunge', patterns: ['grunge / rugged', 'rugged texture', 'industrial character'] },
  { styleId: 'style-minimal', patterns: ['minimal object', 'maximum negative space', 'one object'] },
  { styleId: 'style-pattern-interrupt', patterns: ['pattern interrupt', 'unexpected composition', 'stop scrolling'] },
  { styleId: 'style-text-first', patterns: ['text-first', 'advertiser\'s layout', 'designed around the copy'] },
  { styleId: 'style-neural-forge', patterns: ['futuristic ai / technology', 'neural network', 'data visualization spheres'] },
  { styleId: 'style-fresh-start', patterns: ['golden sunrise', 'fresh start', 'new beginning'] },
  { styleId: 'style-relentless', patterns: ['epic mythic warrior', 'relentless', 'unstoppable'] },
  { styleId: 'style-epic-realm', patterns: ['medieval fantasy kingdom', 'epic realm'] },
  { styleId: 'style-next-gen', patterns: ['next-generation sci-fi', 'advanced technology cgi'] },
  { styleId: 'style-unleashed-power', patterns: ['dragon fire', 'unleashed power'] },
  { styleId: 'style-concept-poster', patterns: ['concept poster', 'high-end fusion poster'] },
  { styleId: 'style-impact-action', patterns: ['impact action', 'high-intensity cinematic action'] },
  { styleId: 'style-liquid-dream', patterns: ['liquid dream', 'impossible fluid environment'] },
  { styleId: 'style-light-filament', patterns: ['light filament', 'glowing light trails'] },
  { styleId: 'style-particle-burst', patterns: ['particle burst', 'glowing particles'] },
  { styleId: 'style-double-exposure', patterns: ['double exposure'] },
  { styleId: 'style-sketch-overlay', patterns: ['sketch overlay', 'pencil sketch outlines'] },
  { styleId: 'style-mystic-awakening', patterns: ['mystic awakening', 'glowing energy cracks'] },
  { styleId: 'style-surreal', patterns: ['dreamlike surrealist', 'surrealist', 'impossible visual metaphors'] },
];

function getStyleIdFromPrompt(prompt?: string): string | null {
  if (!prompt) return null;
  const lowerPrompt = prompt.toLowerCase();
  const directId = lowerPrompt.match(/style-[a-z0-9-]+/)?.[0];
  if (directId && getImageStyleConfig(directId)) return directId;
  return STYLE_PROMPT_MATCHERS.find(({ patterns }) =>
    patterns.some((pattern) => lowerPrompt.includes(pattern.toLowerCase()))
  )?.styleId ?? null;
}

function getStyleInfo(
  prompt: string | undefined,
  index: number,
  stylePackId?: string,
): { label: string; desc: string; icon: string } {
  // ── Priority 1: use the actual style config if we have the ID ──────────────
  const resolvedStyleInfo = getStyleInfoFromId(stylePackId || getStyleIdFromPrompt(prompt) || '');
  if (resolvedStyleInfo) return resolvedStyleInfo;

  if (stylePackId) {
    const config = getImageStyleConfig(stylePackId);
    if (config) {
      // Map category to a fitting icon
      const categoryIconMap: Record<string, string> = {
        photography: 'ri-camera-lens-line',
        marketing: 'ri-megaphone-line',
        illustration: 'ri-pen-nib-line',
        creative: 'ri-sparkling-line',
      };
      return {
        label: config.label,
        desc: config.description,
        icon: categoryIconMap[config.category] ?? 'ri-image-line',
      };
    }
  }

  // ── Priority 2: parse from image prompt text ───────────────────────────────
  if (prompt) {
    const styleMatch = prompt.match(/STYLE:\s*([^.]+)\./);
    if (styleMatch) {
      const styleText = styleMatch[1];
      for (const info of STYLE_INFO_MAP) {
        if (styleText.includes(info.keyword)) {
          return { label: info.label, desc: info.desc, icon: info.icon };
        }
      }
      // Couldn't match keyword — use the raw extracted name trimmed to 3 words
      const shortName = styleText.split(' ').slice(0, 3).join(' ');
      return { label: shortName, desc: 'Custom style', icon: 'ri-image-line' };
    }
  }

  // ── Fallback ───────────────────────────────────────────────────────────────
  return FALLBACK_LABELS[index] ?? { label: `Creative ${index + 1}`, desc: 'Ad image', icon: 'ri-image-line' };
}

/* ─── Overlay Layout Variants ────────────────────────────────────────────── */
interface OverlayVariant {
  id: string;
  gradient: string;
  container: string;
  textWrapper: string;
  headlineClass: string;
  subheadlineClass: string;
  ctaClass: string;
  hideSubheadline?: boolean;
}

const OVERLAY_LAYOUT_VARIANTS: OverlayVariant[] = [
  // 1. Classic Bottom Left
  {
    id: 'classic-bottom',
    gradient: 'bg-gradient-to-t from-black/80 via-black/25 to-transparent',
    container: 'absolute inset-0 flex flex-col justify-end pointer-events-none',
    textWrapper: 'relative z-10 px-5 py-6',
    headlineClass: 'text-white font-extrabold text-xl leading-tight mb-1.5 drop-shadow-lg',
    subheadlineClass: 'text-white/80 text-sm font-medium leading-snug mb-4 drop-shadow',
    ctaClass: 'inline-block bg-orange-500 text-white text-sm font-bold px-5 py-2.5 rounded-xl',
  },
  // 2. Centered Hero (full overlay, centered, white pill CTA)
  {
    id: 'center-hero',
    gradient: 'bg-black/52',
    container: 'absolute inset-0 flex flex-col justify-center items-center pointer-events-none',
    textWrapper: 'relative z-10 px-6 py-6 text-center w-full max-w-xs mx-auto',
    headlineClass: 'text-white font-black text-2xl leading-tight mb-2 drop-shadow-lg',
    subheadlineClass: 'text-white/85 text-sm font-medium leading-snug mb-5 drop-shadow',
    ctaClass: 'inline-block bg-white text-stone-900 text-sm font-bold px-6 py-2.5 rounded-full',
  },
  // 3. Top Left (gradient from top, outlined CTA)
  {
    id: 'top-left',
    gradient: 'bg-gradient-to-b from-black/80 via-black/20 to-transparent',
    container: 'absolute inset-0 flex flex-col justify-start pointer-events-none',
    textWrapper: 'relative z-10 px-5 pt-6 pb-4',
    headlineClass: 'text-white font-extrabold text-xl leading-tight mb-1.5 drop-shadow-lg',
    subheadlineClass: 'text-white/80 text-sm font-medium leading-snug mb-4 drop-shadow',
    ctaClass: 'inline-block border-2 border-white text-white text-sm font-bold px-5 py-2 rounded-xl',
  },
  // 4. Bottom Center (gradient to top, centered, pill CTA)
  {
    id: 'bottom-center',
    gradient: 'bg-gradient-to-t from-black/85 via-black/30 to-transparent',
    container: 'absolute inset-0 flex flex-col justify-end items-center pointer-events-none',
    textWrapper: 'relative z-10 px-5 py-6 text-center w-full',
    headlineClass: 'text-white font-extrabold text-xl leading-tight mb-1.5 drop-shadow-lg',
    subheadlineClass: 'text-white/80 text-sm font-medium leading-snug mb-4 drop-shadow',
    ctaClass: 'inline-block bg-orange-500 text-white text-sm font-bold px-5 py-2.5 rounded-full',
  },
  // 5. Left Side Panel (horizontal gradient, vertically centered)
  {
    id: 'left-panel',
    gradient: 'bg-gradient-to-r from-black/85 via-black/35 to-transparent',
    container: 'absolute inset-0 flex flex-col justify-center pointer-events-none',
    textWrapper: 'relative z-10 px-5 py-6 max-w-[58%]',
    headlineClass: 'text-white font-extrabold text-lg leading-tight mb-1.5 drop-shadow-lg',
    subheadlineClass: 'text-white/80 text-xs font-medium leading-snug mb-4 drop-shadow',
    ctaClass: 'inline-block bg-orange-500 text-white text-xs font-bold px-4 py-2 rounded-lg',
  },
  // 6. Minimal Bold (heavy bottom gradient, giant headline, no subheadline, outlined CTA)
  {
    id: 'minimal-bold',
    gradient: 'bg-gradient-to-t from-black/90 via-black/20 to-transparent',
    container: 'absolute inset-0 flex flex-col justify-end pointer-events-none',
    textWrapper: 'relative z-10 px-5 py-6',
    headlineClass: 'text-white font-black text-2xl leading-tight mb-4 drop-shadow-lg tracking-tight',
    subheadlineClass: 'hidden',
    ctaClass: 'inline-block border-2 border-orange-400 text-orange-300 text-sm font-bold px-5 py-2 rounded-full',
    hideSubheadline: true,
  },
];

/* ─── Animation Effects ──────────────────────────────────────────────────── */
type AdAnimation = 'none' | 'ken-burns' | 'ticker' | 'cta-pulse' | 'spotlight' | 'text-reveal';

interface AnimationOption {
  id: AdAnimation;
  label: string;
  icon: string;
  desc: string;
}

const ANIMATION_OPTIONS: AnimationOption[] = [
  { id: 'none',        label: 'None',         icon: 'ri-stop-circle-line',      desc: 'Static image'       },
  { id: 'ken-burns',   label: 'Ken Burns',    icon: 'ri-expand-diagonal-line',  desc: 'Slow zoom & pan'    },
  { id: 'ticker',      label: 'Scroll Ticker',icon: 'ri-arrow-right-double-line',desc: 'Scrolling CTA band' },
  { id: 'cta-pulse',   label: 'CTA Pulse',    icon: 'ri-pulse-line',            desc: 'Button glow ring'   },
  { id: 'spotlight',   label: 'Spotlight',    icon: 'ri-flashlight-line',       desc: 'Light sweep across' },
  { id: 'text-reveal', label: 'Text Reveal',  icon: 'ri-text-direction-l',      desc: 'Text fade-in loop'  },
];

/* ─── Aspect Ratio ───────────────────────────────────────────────────────── */
type AspectRatio = '1:1' | '9:16' | '16:9';

interface RatioOption {
  id: AspectRatio;
  label: string;
  icon: string;
  tailwind: string;
  cssValue: string;
  desc: string;
}

const RATIO_OPTIONS: RatioOption[] = [
  { id: '1:1',  label: '1:1',  icon: 'ri-square-line',    tailwind: 'aspect-square',  cssValue: '1/1',  desc: 'Square'     },
  { id: '9:16', label: '9:16', icon: 'ri-smartphone-line', tailwind: 'aspect-[9/16]', cssValue: '9/16', desc: 'Vertical'   },
  { id: '16:9', label: '16:9', icon: 'ri-tv-line',   tailwind: 'aspect-video',  cssValue: '16/9', desc: 'Horizontal' },
];

/* ─── Platform Presets ───────────────────────────────────────────────────── */
interface PlatformPreset {
  id: string;
  label: string;
  icon: string;
  ratio: AspectRatio;
  overlayVariantId: string;
  ratioDesc: string;
  layoutDesc: string;
}

const PLATFORM_PRESETS: PlatformPreset[] = [
  {
    id: 'facebook-feed',
    label: 'Facebook Feed',
    icon: 'ri-facebook-circle-line',
    ratio: '1:1',
    overlayVariantId: 'classic-bottom',
    ratioDesc: '1:1 Square',
    layoutDesc: 'Classic Bottom',
  },
  {
    id: 'instagram-story',
    label: 'Instagram Story',
    icon: 'ri-instagram-line',
    ratio: '9:16',
    overlayVariantId: 'center-hero',
    ratioDesc: '9:16 Vertical',
    layoutDesc: 'Centered Hero',
  },
  {
    id: 'google-display',
    label: 'Google Display',
    icon: 'ri-google-line',
    ratio: '16:9',
    overlayVariantId: 'left-panel',
    ratioDesc: '16:9 Banner',
    layoutDesc: 'Left Panel',
  },
];

/* ─── HTML export helpers ────────────────────────────────────────────────── */
const VARIANT_EXPORT: Record<string, {
  gradient: string;
  containerStyle: string;
  textStyle: string;
  headlineStyle: string;
  subheadlineStyle: string;
  ctaStyle: string;
}> = {
  'classic-bottom': {
    gradient: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.25) 40%, transparent 100%)',
    containerStyle: 'position:absolute;inset:0;display:flex;flex-direction:column;justify-content:flex-end;pointer-events:none',
    textStyle: 'position:relative;z-index:10;padding:24px 20px',
    headlineStyle: 'color:#fff;font-weight:800;font-size:20px;line-height:1.2;margin:0 0 6px;text-shadow:0 2px 8px rgba(0,0,0,.5)',
    subheadlineStyle: 'color:rgba(255,255,255,.8);font-size:14px;font-weight:500;line-height:1.4;margin:0 0 16px;text-shadow:0 1px 4px rgba(0,0,0,.4)',
    ctaStyle: 'display:inline-block;background:#f97316;color:#fff;font-size:14px;font-weight:700;padding:10px 20px;border-radius:12px;text-decoration:none',
  },
  'center-hero': {
    gradient: 'rgba(0,0,0,0.52)',
    containerStyle: 'position:absolute;inset:0;display:flex;flex-direction:column;justify-content:center;align-items:center;pointer-events:none',
    textStyle: 'position:relative;z-index:10;padding:24px;text-align:center;max-width:280px',
    headlineStyle: 'color:#fff;font-weight:900;font-size:22px;line-height:1.2;margin:0 0 8px;text-shadow:0 2px 8px rgba(0,0,0,.5)',
    subheadlineStyle: 'color:rgba(255,255,255,.85);font-size:14px;font-weight:500;line-height:1.4;margin:0 0 20px;text-shadow:0 1px 4px rgba(0,0,0,.4)',
    ctaStyle: 'display:inline-block;background:#fff;color:#1c1917;font-size:14px;font-weight:700;padding:10px 24px;border-radius:9999px;text-decoration:none',
  },
  'top-left': {
    gradient: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 40%, transparent 100%)',
    containerStyle: 'position:absolute;inset:0;display:flex;flex-direction:column;justify-content:flex-start;pointer-events:none',
    textStyle: 'position:relative;z-index:10;padding:24px 20px',
    headlineStyle: 'color:#fff;font-weight:800;font-size:20px;line-height:1.2;margin:0 0 6px;text-shadow:0 2px 8px rgba(0,0,0,.5)',
    subheadlineStyle: 'color:rgba(255,255,255,.8);font-size:14px;font-weight:500;line-height:1.4;margin:0 0 16px;text-shadow:0 1px 4px rgba(0,0,0,.4)',
    ctaStyle: 'display:inline-block;border:2px solid #fff;color:#fff;font-size:14px;font-weight:700;padding:8px 20px;border-radius:12px;text-decoration:none',
  },
  'bottom-center': {
    gradient: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 40%, transparent 100%)',
    containerStyle: 'position:absolute;inset:0;display:flex;flex-direction:column;justify-content:flex-end;align-items:center;pointer-events:none',
    textStyle: 'position:relative;z-index:10;padding:24px 20px;text-align:center;width:100%',
    headlineStyle: 'color:#fff;font-weight:800;font-size:20px;line-height:1.2;margin:0 0 6px;text-shadow:0 2px 8px rgba(0,0,0,.5)',
    subheadlineStyle: 'color:rgba(255,255,255,.8);font-size:14px;font-weight:500;line-height:1.4;margin:0 0 16px;text-shadow:0 1px 4px rgba(0,0,0,.4)',
    ctaStyle: 'display:inline-block;background:#f97316;color:#fff;font-size:14px;font-weight:700;padding:10px 20px;border-radius:9999px;text-decoration:none',
  },
  'left-panel': {
    gradient: 'linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 40%, transparent 100%)',
    containerStyle: 'position:absolute;inset:0;display:flex;flex-direction:column;justify-content:center;pointer-events:none',
    textStyle: 'position:relative;z-index:10;padding:24px 20px;max-width:58%',
    headlineStyle: 'color:#fff;font-weight:800;font-size:18px;line-height:1.2;margin:0 0 6px;text-shadow:0 2px 8px rgba(0,0,0,.5)',
    subheadlineStyle: 'color:rgba(255,255,255,.8);font-size:12px;font-weight:500;line-height:1.4;margin:0 0 16px;text-shadow:0 1px 4px rgba(0,0,0,.4)',
    ctaStyle: 'display:inline-block;background:#f97316;color:#fff;font-size:12px;font-weight:700;padding:8px 16px;border-radius:8px;text-decoration:none',
  },
  'minimal-bold': {
    gradient: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.2) 40%, transparent 100%)',
    containerStyle: 'position:absolute;inset:0;display:flex;flex-direction:column;justify-content:flex-end;pointer-events:none',
    textStyle: 'position:relative;z-index:10;padding:24px 20px',
    headlineStyle: 'color:#fff;font-weight:900;font-size:24px;line-height:1.15;margin:0 0 16px;text-shadow:0 2px 8px rgba(0,0,0,.5);letter-spacing:-0.5px',
    subheadlineStyle: 'display:none',
    ctaStyle: 'display:inline-block;border:2px solid #fb923c;color:#fdba74;font-size:14px;font-weight:700;padding:8px 20px;border-radius:9999px;text-decoration:none',
  },
};
const ANIMATION_EXPORT_CSS: Record<AdAnimation, { keyframes: string; imageStyle: string; ctaStyle: string; headlineStyle: string; subheadlineStyle: string }> = {
  'none':       { keyframes: '', imageStyle: '', ctaStyle: '', headlineStyle: '', subheadlineStyle: '' },
  'ken-burns':  {
    keyframes: '@keyframes ad-ken-burns{0%{transform:scale(1) translate(0,0)}33%{transform:scale(1.09) translate(-2%,-1%)}66%{transform:scale(1.06) translate(1.5%,-0.5%)}100%{transform:scale(1) translate(0,0)}}',
    imageStyle: 'animation:ad-ken-burns 9s ease-in-out infinite;transform-origin:center center',
    ctaStyle: '', headlineStyle: '', subheadlineStyle: '',
  },
  'cta-pulse':  {
    keyframes: '@keyframes ad-cta-ring{0%{box-shadow:0 0 0 0 rgba(249,115,22,.75)}70%{box-shadow:0 0 0 14px rgba(249,115,22,0)}100%{box-shadow:0 0 0 0 rgba(249,115,22,0)}}',
    imageStyle: '',
    ctaStyle: 'animation:ad-cta-ring 1.6s ease-out infinite',
    headlineStyle: '', subheadlineStyle: '',
  },
  'text-reveal': {
    keyframes: '@keyframes ad-text-reveal{0%{opacity:0;transform:translateY(14px)}18%{opacity:1;transform:translateY(0)}78%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(-10px)}} @keyframes ad-text-reveal-sub{0%{opacity:0;transform:translateY(10px)}36%{opacity:1;transform:translateY(0)}78%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(-8px)}}',
    imageStyle: '',
    ctaStyle: '',
    headlineStyle: 'animation:ad-text-reveal 4s ease-in-out infinite',
    subheadlineStyle: 'animation:ad-text-reveal-sub 4s ease-in-out infinite',
  },
  'ticker': {
    keyframes: '@keyframes ad-ticker-scroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}',
    imageStyle: '', ctaStyle: '', headlineStyle: '', subheadlineStyle: '',
  },
  'spotlight': {
    keyframes: '@keyframes ad-spotlight{0%{transform:translateX(-160%) skewX(-20deg);opacity:0}8%{opacity:0.55}92%{opacity:0.55}100%{transform:translateX(260%) skewX(-20deg);opacity:0}}',
    imageStyle: '', ctaStyle: '', headlineStyle: '', subheadlineStyle: '',
  },
};
const PRESET_CHIPS: { category: string; color: string; chips: { label: string; icon: string }[] }[] = [
  {
    category: 'Lighting',
    color: 'amber',
    chips: [
      { label: 'Warmer lighting', icon: 'ri-sun-line' },
      { label: 'Golden hour glow', icon: 'ri-sun-foggy-line' },
      { label: 'Softer, diffused light', icon: 'ri-contrast-drop-line' },
      { label: 'More dramatic shadows', icon: 'ri-moon-line' },
    ],
  },
  {
    category: 'Atmosphere',
    color: 'sky',
    chips: [
      { label: 'More dramatic sky', icon: 'ri-cloud-windy-line' },
      { label: 'Misty atmosphere', icon: 'ri-mist-line' },
      { label: 'Bright & airy feel', icon: 'ri-sun-line' },
      { label: 'Darker, moodier tone', icon: 'ri-thunderstorms-line' },
    ],
  },
  {
    category: 'Composition',
    color: 'stone',
    chips: [
      { label: 'Remove background clutter', icon: 'ri-scissors-cut-line' },
      { label: 'Clean minimal background', icon: 'ri-artboard-line' },
      { label: 'More negative space', icon: 'ri-expand-width-line' },
      { label: 'Tighter crop on subject', icon: 'ri-crop-line' },
    ],
  },
  {
    category: 'Color',
    color: 'teal',
    chips: [
      { label: 'More vibrant colors', icon: 'ri-palette-line' },
      { label: 'Cinematic color grade', icon: 'ri-film-line' },
      { label: 'Rich, saturated tones', icon: 'ri-contrast-fill' },
      { label: 'Muted & desaturated', icon: 'ri-contrast-drop-2-line' },
    ],
  },
];

const CHIP_STYLES: Record<string, { bg: string; text: string; border: string; hoverBg: string }> = {
  amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', hoverBg: 'hover:bg-amber-100' },
  sky:   { bg: 'bg-sky-50',   text: 'text-sky-700',   border: 'border-sky-200',   hoverBg: 'hover:bg-sky-100'   },
  stone: { bg: 'bg-stone-100', text: 'text-stone-600', border: 'border-stone-200', hoverBg: 'hover:bg-stone-200' },
  teal:  { bg: 'bg-teal-50',  text: 'text-teal-700',  border: 'border-teal-200',  hoverBg: 'hover:bg-teal-100'  },
};

interface ResultsPanelProps {
  result: AdResult | null;
  isGenerating: boolean;
  onRegenerate: () => void;
  onSave: () => void;
  onRegenerateCopy?: () => Promise<void>;
  platform: string;
  businessName: string;
  adsPerBatch?: number;
  businessContext?: BusinessContext;
  logoUrl?: string;
  // Session-persisted edit state (lifted up so it survives tab switches)
  editedUrls: Record<number, string>;
  editHistory: Record<number, EditEntry[]>;
  onEditedUrlUpdate: (idx: number, url: string) => void;
  onEditHistoryUpdate: (idx: number, entries: EditEntry[]) => void;
  onRevertToOriginal: (idx: number) => void;
  onClearSession: () => void;
  onRegenerateSingle?: (idx: number) => Promise<void>;
}

/* ─── Ad Creative Preview ────────────────────────────────────────────────── */
const GENERATION_STAGES = [
  {
    threshold: 0,
    label: 'Preparing campaign',
    detail: 'Reading your profile, offer, audience, and platform.',
    icon: 'ri-profile-line',
  },
  {
    threshold: 18,
    label: 'Writing ad copy',
    detail: 'Drafting hooks, headlines, primary text, and CTAs.',
    icon: 'ri-quill-pen-line',
  },
  {
    threshold: 40,
    label: 'Building image prompts',
    detail: 'Turning the campaign into creative directions and visual concepts.',
    icon: 'ri-lightbulb-flash-line',
  },
  {
    threshold: 58,
    label: 'Generating creatives',
    detail: 'Creating image variations and matching them to your selected style.',
    icon: 'ri-image-2-line',
  },
  {
    threshold: 82,
    label: 'Finalizing results',
    detail: 'Saving the generation and preparing the editable preview.',
    icon: 'ri-check-double-line',
  },
];

function GenerationProgressState({
  platform,
  adsPerBatch,
}: {
  platform: string;
  adsPerBatch: number;
}) {
  const estimatedSeconds = adsPerBatch <= 1 ? 35 : adsPerBatch <= 3 ? 60 : 95;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    setElapsedSeconds(0);
    const timer = window.setInterval(() => {
      setElapsedSeconds((seconds) => seconds + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [adsPerBatch, platform]);

  const progress = Math.min(92, Math.round((elapsedSeconds / estimatedSeconds) * 88) + 4);
  const activeStageIndex = GENERATION_STAGES.reduce(
    (activeIndex, stage, index) => (progress >= stage.threshold ? index : activeIndex),
    0,
  );
  const activeStage = GENERATION_STAGES[activeStageIndex];
  const remainingSeconds = Math.max(0, estimatedSeconds - elapsedSeconds);
  const completedCreatives = Math.min(
    adsPerBatch,
    Math.max(0, Math.floor((progress - 50) / Math.max(1, 42 / adsPerBatch))),
  );

  return (
    <div className="bg-white rounded-2xl border border-stone-100 h-full min-h-[500px] flex flex-col items-center justify-center px-5 py-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative w-12 h-12 flex items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-400 shrink-0">
              <i className="ri-flashlight-fill text-white text-xl" />
              <span className="absolute -right-1 -bottom-1 w-4 h-4 rounded-full bg-white flex items-center justify-center">
                <span className="w-2.5 h-2.5 rounded-full bg-orange-400 animate-ping" />
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-base font-bold text-stone-900">Generating your ads</p>
              <p className="text-xs text-stone-400 truncate">
                {platform} creative set - {adsPerBatch} image{adsPerBatch !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xl font-black text-stone-900">{progress}%</p>
            <p className="text-xs text-stone-400">{remainingSeconds > 0 ? `~${remainingSeconds}s` : 'Wrapping up'}</p>
          </div>
        </div>

        <div className="h-3 rounded-full bg-stone-100 overflow-hidden border border-stone-200">
          <div
            className="h-full rounded-full bg-gradient-to-r from-orange-500 via-amber-400 to-teal-400 transition-all duration-700 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="mt-5 rounded-xl border border-orange-100 bg-orange-50 px-4 py-3 flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-orange-100 shrink-0">
            <i className={`${activeStage.icon} text-orange-500 text-sm`} />
          </div>
          <div>
            <p className="text-sm font-bold text-stone-900">{activeStage.label}</p>
            <p className="text-xs text-orange-700 mt-0.5 leading-relaxed">{activeStage.detail}</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {GENERATION_STAGES.map((stage, index) => {
            const isDone = index < activeStageIndex;
            const isActive = index === activeStageIndex;
            return (
              <div
                key={stage.label}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs transition-colors ${
                  isDone
                    ? 'border-teal-100 bg-teal-50 text-teal-700'
                    : isActive
                    ? 'border-orange-200 bg-orange-50 text-orange-700'
                    : 'border-stone-100 bg-stone-50 text-stone-400'
                }`}
              >
                <i className={`${isDone ? 'ri-check-line' : stage.icon} text-sm shrink-0`} />
                <span className="font-semibold truncate">{stage.label}</span>
              </div>
            );
          })}
        </div>

        {adsPerBatch > 1 && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-stone-200 bg-stone-50 px-4 py-3">
            <div>
              <p className="text-xs font-bold text-stone-700">Creative batch</p>
              <p className="text-xs text-stone-400 mt-0.5">Images may finish at slightly different times.</p>
            </div>
            <span className="text-xs font-black text-stone-900">
              {completedCreatives}/{adsPerBatch}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function AdCreativePreview({
  result,
  editedUrls,
  editHistory,
  onEditedUrlUpdate,
  onEditHistoryUpdate,
  onRevertToOriginal,
  onRegenerateSingle,
  logoUrl,
}: {
  result: AdResult;
  editedUrls: Record<number, string>;
  editHistory: Record<number, EditEntry[]>;
  onEditedUrlUpdate: (idx: number, url: string) => void;
  onEditHistoryUpdate: (idx: number, entries: EditEntry[]) => void;
  onRevertToOriginal: (idx: number) => void;
  onRegenerateSingle?: (idx: number) => Promise<void>;
  logoUrl?: string;
}) {
  const imagePrompts = result.imagePrompts ?? [];
  const navigate = useNavigate();

  // Build ordered list of image sources from multi-image arrays or legacy single
  const imageSources = useMemo(() => {
    const sources: string[] = [];
    if (result.imageStorageUrls && result.imageStorageUrls.length > 0) {
      sources.push(...result.imageStorageUrls);
    } else if (result.imageUrls && result.imageUrls.length > 0) {
      sources.push(...result.imageUrls);
    } else {
      const legacy =
        result.imageUrl ??
        (result.imageBase64 ? `data:image/png;base64,${result.imageBase64}` : null);
      if (legacy) sources.push(legacy);
    }
    return sources.filter(Boolean);
  }, [result.imageBase64, result.imageStorageUrls, result.imageUrl, result.imageUrls]);

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [imgLoadedMap, setImgLoadedMap] = useState<Record<number, boolean>>({});
  const [imgErrorMap, setImgErrorMap] = useState<Record<number, boolean>>({});

  // When the image set changes (new generation or loading from history), reset
  // the selected index and clear stale load/error maps. This prevents the case
  // where a previously-selected index is out of bounds for the new image set
  // (e.g. had 3 images selected at index 2, load a 1-image history item →
  //  imageSources[2] is undefined → image is invisible).
  // When the image set changes (new generation or loading from history), reset
  // the selected index and clear stale load/error maps. This prevents the case
  // where a previously-selected index is out of bounds for the new image set
  // (e.g. had 3 images selected at index 2, load a 1-image history item →
  //  imageSources[2] is undefined → image is invisible).
  // useMemo ensures the key string is stable between re-renders so the effect
  // only fires when the actual image set genuinely changes.
  const imageCountKey = useMemo(
    () => imageSources.join('::'),
    [imageSources]
  );
  useEffect(() => {
    setSelectedIdx(0);
    setImgLoadedMap({});
    setImgErrorMap({});

    imageSources.forEach((src, idx) => {
      const img = new Image();
      img.onload = () => {
        setImgLoadedMap((prev) => ({ ...prev, [idx]: true }));
      };
      img.onerror = () => {
        setImgErrorMap((prev) => ({ ...prev, [idx]: true }));
      };
      img.src = src;
      if (img.complete && img.naturalWidth > 0) {
        setImgLoadedMap((prev) => ({ ...prev, [idx]: true }));
      }
    });
  }, [imageCountKey, imageSources]);
  const [showOverlay, setShowOverlay] = useState(true);
  const [overlayMode, setOverlayMode] = useState<'ai' | 'custom'>('ai');
  const [customTextType, setCustomTextType] = useState<'headline' | 'hook'>('headline');
  const [customTextIdx, setCustomTextIdx] = useState(0);
  const [customSubheadlineIdx, setCustomSubheadlineIdx] = useState(-1);
  const [customCtaIdx, setCustomCtaIdx] = useState(0);

  // Randomly assign a distinct layout variant per image slot on mount (supports up to 6 slots)
  const [layoutVariants, setLayoutVariants] = useState<number[]>(() => {
    const n = OVERLAY_LAYOUT_VARIANTS.length;
    const shuffled = Array.from({ length: n }, (_, i) => i).sort(() => Math.random() - 0.5);
    return Array.from({ length: 6 }, (_, i) => shuffled[i % n]);
  });

  // Edit panel UI state (ephemeral — just controls whether the panel is open)
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');
  const [editMode, setEditMode] = useState<EditModeId>('basic');
  const [isApplyingEdit, setIsApplyingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editFallbackMsg, setEditFallbackMsg] = useState<string | null>(null);
  const [showAllChips, setShowAllChips] = useState(false);
  const { creditsRemaining, hasEnoughCredits, refetch: refetchCredits } = useCreditsContext();

  // Reference image state (for edit panel)
  const [refImageBase64, setRefImageBase64] = useState<string | null>(null);
  const [refImagePreview, setRefImagePreview] = useState<string | null>(null);
  const [refImagePrompt, setRefImagePrompt] = useState('');
  const refFileInputRef = useRef<HTMLInputElement>(null);

  const handleRefImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setRefImagePreview(dataUrl);
      const base64 = dataUrl.split(',')[1] ?? null;
      setRefImageBase64(base64);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const handleRemoveRefImage = useCallback(() => {
    setRefImageBase64(null);
    setRefImagePreview(null);
    setRefImagePrompt('');
  }, []);

  // Animation effects state
  const [selectedAnimation, setSelectedAnimation] = useState<AdAnimation>('none');
  const [showAnimPanel, setShowAnimPanel] = useState(false);
  const previewFrameRef = useRef<HTMLDivElement>(null);
  const [isCapturingOverlay, setIsCapturingOverlay] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);

  // Aspect ratio + HTML export state
  const [selectedRatio, setSelectedRatio] = useState<AspectRatio>('1:1');
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [videoAdOpen, setVideoAdOpen] = useState(false);

  // Logo overlay state
  const [showLogoOverlay, setShowLogoOverlay] = useState(false);
  const [logoPosition, setLogoPosition] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>('bottom-right');
  const [logoSize, setLogoSize] = useState<'sm' | 'md' | 'lg'>('md');

  const logoPositionClasses: Record<string, string> = {
    'top-left': 'top-3 left-3',
    'top-right': 'top-3 right-3',
    'bottom-left': 'bottom-3 left-3',
    'bottom-right': 'bottom-3 right-3',
  };
  const logoSizeClasses: Record<string, string> = {
    sm: 'w-10 h-10',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
  };

  const handleSelectPreset = (preset: PlatformPreset) => {
    setSelectedPreset(preset.id);
    setSelectedRatio(preset.ratio);
    const variantIdx = OVERLAY_LAYOUT_VARIANTS.findIndex((v) => v.id === preset.overlayVariantId);
    if (variantIdx >= 0) {
      setLayoutVariants((prev) => {
        const next = [...prev];
        next[selectedIdx] = variantIdx;
        return next;
      });
    }
  };

  const generateHtmlExport = (): string => {
    const variantId = OVERLAY_LAYOUT_VARIANTS[layoutVariants[selectedIdx] ?? 0]?.id ?? 'classic-bottom';
    const exportStyle = VARIANT_EXPORT[variantId] ?? VARIANT_EXPORT['classic-bottom'];
    const animExport = ANIMATION_EXPORT_CSS[selectedAnimation];
    const ratioOpt = RATIO_OPTIONS.find((r) => r.id === selectedRatio) ?? RATIO_OPTIONS[0];
    const imageUrl = getActiveUrl(selectedIdx) ?? '';
    const tickerItems = Array(6).fill(null).map((_, i) =>
      `<span key="${i}" style="display:inline-flex;align-items:center;gap:12px;font-size:12px;color:#fff;font-weight:700;text-transform:uppercase;letter-spacing:.1em;margin:0 24px">★ ${displayCta || 'Learn More'} · ${displayHeadline || 'Act Now'}</span>`
    ).join('');

    return `<!-- Ad Creative — Generated by Readdy.ai -->
<div style="position:relative;width:100%;max-width:600px;aspect-ratio:${ratioOpt.cssValue};overflow:hidden;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;border-radius:16px">
  ${animExport.keyframes ? `<style>${animExport.keyframes}</style>` : ''}
  <img
    src="${imageUrl}"
    alt="Ad Creative"
    style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover${animExport.imageStyle ? ';' + animExport.imageStyle : ''}"
  />
  ${selectedAnimation === 'spotlight' ? `<div style="position:absolute;inset:0;overflow:hidden;pointer-events:none"><div style="position:absolute;top:0;left:0;width:40%;height:100%;background:linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.35) 50%,transparent 70%);animation:ad-spotlight 3.2s ease-in-out infinite"></div></div>` : ''}
  ${displayHeadline || displayCta ? `<div style="${exportStyle.containerStyle}">
    <div style="position:absolute;inset:0;background:${exportStyle.gradient}"></div>
    <div style="${exportStyle.textStyle}">
      ${displayHeadline ? `<p style="${exportStyle.headlineStyle}${animExport.headlineStyle ? ';' + animExport.headlineStyle : ''}">${displayHeadline}</p>` : ''}
      ${displaySubheadline && exportStyle.subheadlineStyle !== 'display:none' ? `<p style="${exportStyle.subheadlineStyle}${animExport.subheadlineStyle ? ';' + animExport.subheadlineStyle : ''}">${displaySubheadline}</p>` : ''}
      ${displayCta ? `<a href="#" style="${exportStyle.ctaStyle}${animExport.ctaStyle ? ';' + animExport.ctaStyle : ''}">${displayCta}</a>` : ''}
    </div>
  </div>` : ''}
  ${selectedAnimation === 'ticker' ? `<div style="position:absolute;bottom:0;left:0;right:0;background:rgba(249,115,22,.9);overflow:hidden;padding:8px 0">
    <div style="display:flex;white-space:nowrap;animation:ad-ticker-scroll 14s linear infinite;width:max-content">
      ${tickerItems}
    </div>
  </div>` : ''}
</div>`;
  };

  const handleCopyHtml = () => {
    navigator.clipboard.writeText(generateHtmlExport());
    setCopiedHtml(true);
    setTimeout(() => setCopiedHtml(false), 2500);
  };

  // Per-thumbnail regeneration loading state
  const [regenLoading, setRegenLoading] = useState<Record<number, boolean>>({});
  const [regenError, setRegenError] = useState<Record<number, string>>({});

  const getActiveUrl = (idx: number) => editedUrls[idx] ?? imageSources[idx] ?? null;

  const activeSrc = getActiveUrl(selectedIdx);
  const imgLoaded = imgLoadedMap[selectedIdx] ?? false;
  const imgError = imgErrorMap[selectedIdx] ?? false;

  const aiOverlay = result.overlayText;
  const customTexts = customTextType === 'headline' ? result.headlines : result.hooks;
  const customText = customTexts[customTextIdx] ?? '';
  const customCta = result.ctas[customCtaIdx] ?? '';

  const displayHeadline = overlayMode === 'ai' ? (aiOverlay?.headline ?? result.headlines[0] ?? '') : customText;
  const displaySubheadline =
    overlayMode === 'ai'
      ? (aiOverlay?.subheadline ?? '')
      : customSubheadlineIdx >= 0
      ? (result.subheadlines?.[customSubheadlineIdx] ?? '')
      : '';
  const displayCta = overlayMode === 'ai' ? (aiOverlay?.cta ?? result.ctas[0] ?? '') : customCta;

  const handleDownload = () => {
    if (!activeSrc) return;
    downloadFile(activeSrc, `ad-creative-${selectedIdx + 1}-${Date.now()}.jpg`);
  };

  const handleDownloadWithOverlays = async () => {
    const node = previewFrameRef.current;
    if (!node) {
      setCaptureError('Preview not ready. Please try again.');
      return;
    }
    setCaptureError(null);
    setIsCapturingOverlay(true);
    const err = await captureNodeAsPng(node, `ad-with-overlays-${selectedIdx + 1}-${Date.now()}.png`);
    setIsCapturingOverlay(false);
    if (err) {
      setCaptureError(err);
      setTimeout(() => setCaptureError(null), 8000);
    }
  };

  const handleOpenEditor = () => {
    if (!activeSrc) return;
    navigate('/dashboard/editor', {
      state: {
        imageUrl: activeSrc,
        aspectRatio: selectedRatio,
        initialHeadline: displayHeadline || undefined,
        initialSubheadline: displaySubheadline || undefined,
        initialCta: displayCta || undefined,
      },
    });
  };

  const handleApplyEdit = async () => {
    if (!editPrompt.trim() || !activeSrc || isApplyingEdit) return;
    const editModeConfig = getEditModeConfig(editMode);
    if (!hasEnoughCredits(editModeConfig.credits)) {
      setEditError(`Not enough credits. This edit requires ${editModeConfig.credits} credits. Please upgrade or purchase more.`);
      return;
    }
    setIsApplyingEdit(true);
    setEditError(null);
    try {
      // imageKey = original generated URL (stable, before any edits) — used for per-ad free edit tracking
      const imageKey = imageSources[selectedIdx] ?? activeSrc;

      // Use direct fetch for full control over error extraction
      const response = await fetch(EDIT_AD_IMAGE_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({
          imageUrl: activeSrc,
          editPrompt: editPrompt.trim(),
          creativeIndex: selectedIdx,
          imageKey,
          editMode,
          provider: editModeConfig.provider,
          referenceImageBase64: refImageBase64 ?? undefined,
          referenceImagePrompt: refImageBase64 && refImagePrompt.trim() ? refImagePrompt.trim() : undefined,
        }),
      });

      const data = await response.json() as Record<string, unknown>;

      if (!response.ok) {
        const msg = typeof data?.error === 'string' ? data.error : `Edit failed (${response.status})`;
        throw new Error(msg);
      }
      if (data?.error) throw new Error(String(data.error));
      if (!data?.imageUrl) throw new Error('No image returned');

      const newUrl = data.imageUrl as string;
      onEditedUrlUpdate(selectedIdx, newUrl);
      setImgLoadedMap((prev) => ({ ...prev, [selectedIdx]: false }));
      const prevHistory = editHistory[selectedIdx] ?? [];
      onEditHistoryUpdate(selectedIdx, [...prevHistory, { prompt: editPrompt.trim(), url: newUrl }]);
      setEditPrompt('');

      // Non-blocking fallback notice
      if (data.fallbackUsed && data.fallbackMessage) {
        setEditFallbackMsg(data.fallbackMessage as string);
        setTimeout(() => setEditFallbackMsg(null), 6000);
      }
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsApplyingEdit(false);
    }
  };

  const handleRevertToVersion = (url: string) => {
    onEditedUrlUpdate(selectedIdx, url);
    setImgLoadedMap((prev) => ({ ...prev, [selectedIdx]: false }));
  };

  const handleRevertToOriginal = () => {
    onRevertToOriginal(selectedIdx);
    setImgLoadedMap((prev) => ({ ...prev, [selectedIdx]: false }));
  };

  const handleRegenerateThumbnail = async (e: React.MouseEvent, idx: number) => {
    e.stopPropagation();
    if (!onRegenerateSingle || regenLoading[idx]) return;
    setRegenLoading((prev) => ({ ...prev, [idx]: true }));
    setRegenError((prev) => ({ ...prev, [idx]: '' }));
    // Clear loaded state so new image triggers re-load
    setImgLoadedMap((prev) => ({ ...prev, [idx]: false }));
    try {
      await onRegenerateSingle(idx);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Regeneration failed. Please try again.';
      setRegenError((prev) => ({ ...prev, [idx]: msg }));
      // Restore loaded state so old image is still visible
      setImgLoadedMap((prev) => ({ ...prev, [idx]: true }));
    } finally {
      setRegenLoading((prev) => ({ ...prev, [idx]: false }));
    }
  };

  const currentHistory = editHistory[selectedIdx] ?? [];
  const hasLocalEdit = editedUrls[selectedIdx] !== undefined;

  if (imageSources.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50 px-6 py-10 flex flex-col items-center justify-center text-center gap-4">
        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-stone-100 to-stone-50 border border-stone-200">
          <i className="ri-image-line text-stone-400 text-2xl" />
        </div>
        <div>
          <p className="text-sm font-bold text-stone-600">Creative preview unavailable for this generation</p>
          <p className="text-xs text-stone-400 mt-1 leading-relaxed max-w-xs">
            The text copy above is ready to use. Image generation may have been unavailable.
          </p>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-stone-400 bg-stone-100 border border-stone-200 px-3 py-1.5 rounded-full">
          <i className="ri-refresh-line text-xs" />
          Try regenerating to get a creative image
        </span>
      </div>
    );
  }

  const activeCreativeInfo = getStyleInfo(
    imagePrompts[selectedIdx],
    selectedIdx,
    result.stylePackIds?.[selectedIdx] ?? result.autoSelectedStyleIds?.[selectedIdx]
  );

  return (
    <div className="space-y-3">
      {/* Creative picker tabs */}
      {imageSources.length > 1 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-stone-500 uppercase tracking-widest">Creative Options</span>
            <span className="text-xs text-stone-400">{imageSources.length} generated</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {imageSources.map((_, idx) => {
              const src = getActiveUrl(idx);
              const info = getStyleInfo(
                imagePrompts[idx],
                idx,
                result.stylePackIds?.[idx] ?? result.autoSelectedStyleIds?.[idx]
              );
              const isActive = selectedIdx === idx;
              const isLoaded = imgLoadedMap[idx] ?? false;
              const hasEdit = editedUrls[idx] !== undefined;
              const historyCount = editHistory[idx]?.length ?? 0;
              const isRegenerating = regenLoading[idx] ?? false;
              return (
                <button
                  key={idx}
                  onClick={() => { setSelectedIdx(idx); setShowEditPanel(false); setEditError(null); }}
                  className={`relative flex flex-col items-center rounded-xl border-2 overflow-hidden transition-all duration-200 cursor-pointer group ${
                    isActive
                      ? 'border-orange-400 shadow-sm'
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="w-full aspect-square bg-stone-100 overflow-hidden relative">
                    {src && (
                      <img
                        src={src}
                        alt={`Creative option ${idx + 1}`}
                        className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded && !isRegenerating ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => setImgLoadedMap((prev) => ({ ...prev, [idx]: true }))}
                        onError={() => setImgErrorMap((prev) => ({ ...prev, [idx]: true }))}
                      />
                    )}
                    {/* Loading placeholder */}
                    {(!isLoaded || isRegenerating) && !(imgErrorMap[idx]) && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 animate-pulse bg-stone-100">
                        {isRegenerating ? (
                          <>
                            <i className="ri-loader-4-line text-orange-400 text-xl animate-spin" />
                            <span className="text-xs text-stone-400 font-medium px-2 text-center leading-tight">Re-rolling...</span>
                          </>
                        ) : (
                          <i className="ri-image-line text-stone-300 text-xl" />
                        )}
                      </div>
                    )}
                    {/* Error state badge */}
                    {regenError[idx] && !isRegenerating && (
                      <div className="absolute inset-0 bg-red-50/90 flex flex-col items-center justify-center gap-1 p-2">
                        <i className="ri-error-warning-line text-red-500 text-lg" />
                        <span className="text-xs text-red-600 font-semibold text-center leading-tight">Failed</span>
                      </div>
                    )}
                    {/* Selected checkmark */}
                    {isActive && !isRegenerating && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-orange-500 text-white">
                        <i className="ri-check-line text-xs" />
                      </div>
                    )}
                    {/* Edit count badge */}
                    {hasEdit && historyCount > 0 && !isRegenerating && (
                      <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 bg-stone-800/80 text-white px-1.5 py-0.5 rounded-full">
                        <i className="ri-edit-line text-xs" />
                        <span className="text-xs font-semibold">{historyCount}</span>
                      </div>
                    )}
                    {/* Regen button — visible on hover, always if loading */}
                    {onRegenerateSingle && !isRegenerating && (
                      <div className={`absolute bottom-1.5 right-1.5 transition-opacity duration-150 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        <button
                          onClick={(e) => handleRegenerateThumbnail(e, idx)}
                          title={`Re-roll ${info.label} creative`}
                          className="w-6 h-6 flex items-center justify-center rounded-full bg-stone-900/75 hover:bg-orange-500 text-white transition-colors duration-150 cursor-pointer"
                        >
                          <i className="ri-refresh-line text-xs" />
                        </button>
                      </div>
                    )}
                    {/* Spinning overlay while regenerating */}
                    {isRegenerating && (
                      <div className="absolute inset-0 bg-stone-900/30 flex items-center justify-center">
                        <div className="bg-white rounded-xl px-3 py-2 flex items-center gap-2 shadow-sm">
                          <i className="ri-loader-4-line text-orange-500 animate-spin text-sm" />
                          <span className="text-xs font-semibold text-stone-700">Generating...</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Label */}
                  <div className={`w-full px-2 py-1.5 text-center transition-colors ${isActive ? 'bg-orange-50' : 'bg-white'}`}>
                    <div className={`flex items-center justify-center gap-1 text-xs font-bold ${isActive ? 'text-orange-600' : 'text-stone-600'}`}>
                      <div className={`w-3 h-3 flex items-center justify-center`}>
                        <i className={`${info.icon} text-xs`} />
                      </div>
                      <span className="truncate">{info.label}</span>
                    </div>
                    <p className="text-xs text-stone-400 leading-tight truncate mt-0.5">{info.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
          {/* Re-roll hint */}
          {onRegenerateSingle && (
            <p className="text-xs text-stone-400 flex items-center gap-1.5">
              <i className="ri-information-line text-stone-300" />
              Hover any creative and click <i className="ri-refresh-line text-stone-400 mx-0.5" /> to re-roll just that image
            </p>
          )}

          {/* Error message banner for selected slot */}
          {regenError[selectedIdx] && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-lg">
              <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
              <span className="leading-snug flex-1">{regenError[selectedIdx]}</span>
              <button
                onClick={() => setRegenError((prev) => ({ ...prev, [selectedIdx]: '' }))}
                className="ml-auto shrink-0 text-red-400 hover:text-red-600 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Preview / Clean toggle + Aspect Ratio */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-stone-500 uppercase tracking-widest">
              {imageSources.length > 1 ? 'Preview' : 'Ad Mockup'}
            </span>
            {/* Style label badge — always shown */}
            <span className="inline-flex items-center gap-1 bg-stone-100 border border-stone-200 text-stone-600 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className={`${activeCreativeInfo.icon} text-xs`} />
              </div>
              {activeCreativeInfo.label}
            </span>
          </div>
          {/* Overlay toggle */}
          <div className="flex items-center bg-stone-100 rounded-full p-1 gap-0.5">
            <button
              onClick={() => setShowOverlay(true)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                showOverlay ? 'bg-white text-stone-800' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Preview
            </button>
            <button
              onClick={() => setShowOverlay(false)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                !showOverlay ? 'bg-white text-stone-800' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Clean Image
            </button>
          </div>
        </div>

        {/* Platform Presets */}
        <div className="rounded-xl border border-stone-100 bg-stone-50 px-3 py-2.5 space-y-2">
          <div className="flex items-center gap-2">
            <i className="ri-layout-masonry-line text-stone-400 text-xs" />
            <span className="text-xs font-bold text-stone-500 uppercase tracking-widest">Platform Preset</span>
            {selectedPreset && (
              <button
                onClick={() => setSelectedPreset(null)}
                className="ml-auto text-xs text-stone-400 hover:text-stone-600 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-close-line text-xs" />
                Clear
              </button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {PLATFORM_PRESETS.map((preset) => {
              const isActive = selectedPreset === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => handleSelectPreset(preset)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'border-orange-400 bg-orange-50 text-orange-700'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-orange-200 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <i className={`${preset.icon} text-sm ${isActive ? 'text-orange-500' : 'text-stone-400'}`} />
                  <span>{preset.label}</span>
                  {isActive && (
                    <span className="flex items-center gap-1 text-orange-400 font-medium">
                      <i className="ri-check-line text-xs" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {selectedPreset ? (
            <div className="flex items-center gap-3 pt-0.5">
              {(() => {
                const preset = PLATFORM_PRESETS.find((p) => p.id === selectedPreset);
                return preset ? (
                  <>
                    <span className="flex items-center gap-1 text-xs text-stone-500">
                      <i className="ri-aspect-ratio-line text-stone-400 text-xs" />
                      <strong className="text-stone-700">{preset.ratioDesc}</strong>
                    </span>
                    <span className="text-stone-300">·</span>
                    <span className="flex items-center gap-1 text-xs text-stone-500">
                      <i className="ri-layout-line text-stone-400 text-xs" />
                      <strong className="text-stone-700">{preset.layoutDesc}</strong> overlay applied
                    </span>
                  </>
                ) : null;
              })()}
            </div>
          ) : (
            <p className="text-xs text-stone-400 flex items-center gap-1">
              <i className="ri-information-line text-stone-300 text-xs" />
              Pick a platform to auto-set ratio &amp; best overlay layout
            </p>
          )}
        </div>

        {/* Aspect ratio picker */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-stone-400 font-medium shrink-0">Ratio:</span>
          <div className="flex items-center bg-stone-100 rounded-full p-1 gap-0.5">
            {RATIO_OPTIONS.map((r) => (
              <button
                key={r.id}
                onClick={() => { setSelectedRatio(r.id); setSelectedPreset(null); }}
                title={r.desc}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  selectedRatio === r.id ? 'bg-white text-stone-800' : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                <i className={`${r.icon} text-xs`} />
                {r.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-stone-400">{RATIO_OPTIONS.find((r) => r.id === selectedRatio)?.desc}</span>
        </div>
      </div>

      {/* Image frame */}
      <div className="rounded-2xl overflow-hidden border border-stone-200 bg-stone-100 shadow-sm">
        {showOverlay && (
          <div className="flex items-center gap-2.5 px-4 py-2.5 bg-white border-b border-stone-100">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-amber-300 shrink-0" />
            <div className="flex-1">
              <div className="text-xs font-bold text-stone-800">Sponsored</div>
              <div className="text-xs text-stone-400">Paid promotion</div>
            </div>
            <i className="ri-more-fill text-stone-300 text-lg" />
          </div>
        )}

        <div ref={previewFrameRef} className={`relative overflow-hidden transition-all duration-300 ${RATIO_OPTIONS.find((r) => r.id === selectedRatio)?.tailwind ?? 'aspect-square'}`}>
          {/* Main preview loading state */}
          {(regenLoading[selectedIdx] || (!imgLoaded && !imgError)) && (
            <div className="absolute inset-0 bg-stone-100 flex flex-col items-center justify-center gap-3 z-10">
              {regenLoading[selectedIdx] ? (
                <>
                  <div className="relative">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-400">
                      <i className="ri-image-line text-white text-2xl" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border-2 border-white flex items-center justify-center">
                      <span className="w-3 h-3 rounded-full bg-orange-400 animate-ping" />
                    </div>
                  </div>
                  <div className="text-center px-6">
                    <p className="text-sm font-bold text-stone-800 mb-1">Re-generating this creative...</p>
                    <p className="text-xs text-stone-400">This usually takes about 20–30 seconds</p>
                  </div>
                  <div className="flex gap-1.5">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="w-2 h-2 rounded-full bg-orange-400 animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <i className="ri-image-line text-stone-300 text-4xl animate-pulse" />
                  <span className="text-xs text-stone-400">
                    {isApplyingEdit ? 'Applying your edit...' : 'Loading creative...'}
                  </span>
                </>
              )}
            </div>
          )}
          {imgError && !regenLoading[selectedIdx] && (
            <div className="absolute inset-0 bg-stone-100 flex flex-col items-center justify-center gap-3">
              <i className="ri-image-2-line text-stone-300 text-4xl" />
              <span className="text-xs text-stone-400">Image could not be loaded</span>
            </div>
          )}
          {activeSrc && (
            <img
              key={activeSrc}
              src={activeSrc}
              alt="Generated ad creative"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${imgLoaded && !regenLoading[selectedIdx] ? 'opacity-100' : 'opacity-0'}`}
              style={selectedAnimation === 'ken-burns' && imgLoaded ? {
                animation: 'ad-ken-burns 9s ease-in-out infinite',
                transformOrigin: 'center center',
              } : undefined}
              onLoad={() => setImgLoadedMap((prev) => ({ ...prev, [selectedIdx]: true }))}
              onError={() => setImgErrorMap((prev) => ({ ...prev, [selectedIdx]: true }))}
            />
          )}

          {/* Spotlight sweep overlay */}
          {selectedAnimation === 'spotlight' && imgLoaded && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '40%',
                  height: '100%',
                  background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)',
                  animation: 'ad-spotlight 3.2s ease-in-out infinite',
                }}
              />
            </div>
          )}

          {imgLoaded && showOverlay && (() => {
            const variant = OVERLAY_LAYOUT_VARIANTS[layoutVariants[selectedIdx] ?? 0];
            const isCtaPulse = selectedAnimation === 'cta-pulse';
            const isTextReveal = selectedAnimation === 'text-reveal';
            return (
              <div className={variant.container}>
                <div className={`absolute inset-0 ${variant.gradient}`} />
                <div className={variant.textWrapper}>
                  {displayHeadline && (
                    <p
                      className={variant.headlineClass}
                      style={isTextReveal ? { animation: 'ad-text-reveal 4s ease-in-out infinite' } : undefined}
                    >
                      {displayHeadline}
                    </p>
                  )}
                  {!variant.hideSubheadline && displaySubheadline && (
                    <p
                      className={variant.subheadlineClass}
                      style={isTextReveal ? { animation: 'ad-text-reveal-sub 4s ease-in-out infinite' } : undefined}
                    >
                      {displaySubheadline}
                    </p>
                  )}
                  {displayCta && (
                    <span
                      className={variant.ctaClass}
                      style={isCtaPulse ? { animation: 'ad-cta-ring 1.6s ease-out infinite', display: 'inline-block' } : undefined}
                    >
                      {displayCta}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Logo overlay */}
          {logoUrl && showLogoOverlay && imgLoaded && (
            <div className={`absolute ${logoPositionClasses[logoPosition]} pointer-events-none`}>
              <div className={`${logoSizeClasses[logoSize]} rounded-xl overflow-hidden bg-white/90 backdrop-blur-sm p-1`}>
                <img
                  src={logoUrl}
                  alt="Business logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          )}

          {/* Scroll Ticker strip */}
          {selectedAnimation === 'ticker' && imgLoaded && (
            <div className="absolute bottom-0 left-0 right-0 bg-orange-500/90 overflow-hidden py-2">
              <div
                className="flex whitespace-nowrap"
                style={{ animation: 'ad-ticker-scroll 14s linear infinite', width: 'max-content' }}
              >
                {[...Array(6)].map((_, i) => (
                  <span key={i} className="flex items-center gap-3 text-white text-xs font-bold uppercase tracking-widest mx-6">
                    <i className="ri-star-fill text-amber-300 text-xs" />
                    {displayCta || 'Learn More'}
                    <span className="text-white/60">·</span>
                    {displayHeadline || 'Act Now'}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {showOverlay && imgLoaded && (
          <div className="flex items-center gap-4 px-4 py-3 bg-white border-t border-stone-100">
            <div className="flex items-center gap-1.5 text-stone-400 text-xs font-medium">
              <i className="ri-thumb-up-line text-sm" /> Like
            </div>
            <div className="flex items-center gap-1.5 text-stone-400 text-xs font-medium">
              <i className="ri-chat-1-line text-sm" /> Comment
            </div>
            <div className="flex items-center gap-1.5 text-stone-400 text-xs font-medium">
              <i className="ri-share-forward-line text-sm" /> Share
            </div>
          </div>
        )}
      </div>

      {/* Logo Overlay Controls — shown when logo is available */}
      {logoUrl && imgLoaded && (
        <div className="rounded-xl border border-stone-200 overflow-hidden">
          <button
            onClick={() => setShowLogoOverlay((o) => !o)}
            className={`w-full flex items-center gap-3 px-4 py-3 transition-colors cursor-pointer ${
              showLogoOverlay ? 'bg-orange-50 border-b border-orange-100' : 'bg-stone-50 hover:bg-stone-100'
            }`}
          >
            <div className={`w-7 h-7 flex items-center justify-center rounded-lg border shrink-0 transition-colors ${
              showLogoOverlay ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-stone-200 text-stone-500'
            }`}>
              <i className="ri-image-edit-line text-sm" />
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-stone-800">Logo Overlay</span>
                {showLogoOverlay && (
                  <span className="text-xs text-orange-600 font-semibold bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded-full">Active</span>
                )}
              </div>
              <p className="text-xs text-stone-400 mt-0.5">Place your business logo on the ad image</p>
            </div>
            <i className={`${showLogoOverlay ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-stone-400 text-lg shrink-0`} />
          </button>

          {showLogoOverlay && (
            <div className="px-4 py-4 bg-white space-y-4">
              {/* Logo preview */}
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl border border-stone-200 overflow-hidden bg-stone-50 p-1 shrink-0">
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-stone-700">Your business logo</p>
                  <p className="text-xs text-stone-400 mt-0.5">Displayed as an overlay on the ad image</p>
                </div>
              </div>

              {/* Position picker */}
              <div>
                <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Position</p>
                <div className="grid grid-cols-2 gap-2">
                  {(['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const).map((pos) => {
                    const labels: Record<string, string> = {
                      'top-left': 'Top Left',
                      'top-right': 'Top Right',
                      'bottom-left': 'Bottom Left',
                      'bottom-right': 'Bottom Right',
                    };
                    const icons: Record<string, string> = {
                      'top-left': 'ri-arrow-left-up-line',
                      'top-right': 'ri-arrow-right-up-line',
                      'bottom-left': 'ri-arrow-left-down-line',
                      'bottom-right': 'ri-arrow-right-down-line',
                    };
                    return (
                      <button
                        key={pos}
                        onClick={() => setLogoPosition(pos)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-xs font-semibold transition-all cursor-pointer ${
                          logoPosition === pos
                            ? 'border-orange-400 bg-orange-50 text-orange-700'
                            : 'border-stone-200 bg-white text-stone-600 hover:border-orange-200'
                        }`}
                      >
                        <i className={`${icons[pos]} text-sm`} />
                        {labels[pos]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Size picker */}
              <div>
                <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Size</p>
                <div className="flex items-center bg-stone-100 rounded-full p-1 gap-0.5">
                  {(['sm', 'md', 'lg'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setLogoSize(s)}
                      className={`flex-1 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        logoSize === s ? 'bg-white text-stone-800' : 'text-stone-500 hover:text-stone-700'
                      }`}
                    >
                      {s === 'sm' ? 'Small' : s === 'md' ? 'Medium' : 'Large'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Edit Image Panel ─────────────────────────────────────────────── */}
      {imgLoaded && (
        <div className="rounded-xl border border-stone-200 overflow-hidden">
          {/* Toggle button */}
          <button
            onClick={() => { setShowEditPanel((o) => !o); setEditError(null); }}
            className={`w-full flex items-center gap-3 transition-colors cursor-pointer ${
              showEditPanel ? 'bg-orange-50 border-b border-orange-100' : 'bg-stone-50 hover:bg-stone-100'
            }`}
          >
            <div className={`w-7 h-7 flex items-center justify-center rounded-lg border shrink-0 transition-colors ${
              showEditPanel ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-stone-200 text-stone-500'
            }`}>
              <i className="ri-magic-line text-sm" />
            </div>
            <div className="flex-1 text-left">
              <span className="text-xs font-bold text-stone-800">Edit this image</span>
              {hasLocalEdit && (
                <span className="ml-2 text-xs text-orange-600 font-semibold bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded-full">
                  {currentHistory.length} edit{currentHistory.length !== 1 ? 's' : ''} applied
                </span>
              )}
              <p className="text-xs text-stone-400 mt-0.5">Describe changes and the AI will apply them to this creative</p>
            </div>
            <i className={`${showEditPanel ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-stone-400 text-lg shrink-0`} />
          </button>

          {/* Edit panel body */}
          {showEditPanel && (
            <div className="px-4 py-4 bg-white space-y-4">
              {/* Preset suggestion chips */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Quick suggestions</p>
                  <button
                    onClick={() => setShowAllChips((p) => !p)}
                    className="text-xs text-stone-400 hover:text-orange-500 transition-colors cursor-pointer flex items-center gap-1"
                  >
                    {showAllChips ? (
                      <><i className="ri-arrow-up-s-line text-sm" /> Less</>
                    ) : (
                      <><i className="ri-arrow-down-s-line text-sm" /> More</>
                    )}
                  </button>
                </div>

                {/* Always-visible first row of chips (first 2 categories when collapsed) */}
                <div className="space-y-2">
                  {PRESET_CHIPS.slice(0, showAllChips ? PRESET_CHIPS.length : 2).map((group) => {
                    const style = CHIP_STYLES[group.color] ?? CHIP_STYLES.stone;
                    return (
                      <div key={group.category} className="space-y-1">
                        <p className="text-xs text-stone-400 font-medium">{group.category}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {group.chips.map((chip) => (
                            <button
                              key={chip.label}
                              onClick={() => {
                                setEditPrompt((prev) =>
                                  prev.trim() ? `${prev.trim()}, ${chip.label.toLowerCase()}` : chip.label
                                );
                              }}
                              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-all duration-150 cursor-pointer whitespace-nowrap ${style.bg} ${style.text} ${style.border} ${style.hoverBg}`}
                            >
                              <i className={`${chip.icon} text-xs`} />
                              {chip.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-stone-100" />

              {/* Prompt input */}
              <div>
                <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
                  Describe your edit
                </label>
                <textarea
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  placeholder="e.g. Change the lighting to golden hour sunset, add a dramatic sky, make the background more vibrant, change the color scheme to warm tones..."
                  rows={3}
                  maxLength={500}
                  className="w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200 resize-none"
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-stone-400">Be specific — mention colors, lighting, composition, mood, etc.</p>
                  <span className="text-xs text-stone-400">{editPrompt.length}/500</span>
                </div>
              </div>

              {/* ── Reference Image Upload ── */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider">
                    Reference Image
                  </label>
                  <span className="text-xs text-stone-400 font-medium bg-stone-100 px-1.5 py-0.5 rounded-full">Optional</span>
                </div>
                <p className="text-xs text-stone-400 mb-3 leading-relaxed">
                  Upload a product, logo, or scene you want the AI to incorporate or recreate in the edited image.
                </p>

                {!refImagePreview ? (
                  <button
                    type="button"
                    onClick={() => refFileInputRef.current?.click()}
                    className="w-full flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-xl border-2 border-dashed border-stone-200 bg-stone-50 hover:border-orange-300 hover:bg-orange-50 transition-all duration-200 cursor-pointer group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-200 group-hover:bg-orange-100 transition-colors">
                      <i className="ri-image-add-line text-stone-500 group-hover:text-orange-500 text-lg transition-colors" />
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-semibold text-stone-600 group-hover:text-orange-600 transition-colors">Upload reference image</p>
                      <p className="text-xs text-stone-400 mt-0.5">PNG, JPG, WEBP · Click to browse</p>
                    </div>
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-100">
                      <img
                        src={refImagePreview}
                        alt="Reference"
                        className="w-full max-h-40 object-contain bg-stone-50"
                      />
                      <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        <i className="ri-image-line text-xs" />
                        Reference uploaded
                      </div>
                      <div className="absolute top-2 right-2 flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => refFileInputRef.current?.click()}
                          className="flex items-center gap-1 bg-white/90 hover:bg-white text-stone-600 text-xs font-semibold px-2 py-1 rounded-lg border border-stone-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-refresh-line text-xs" />
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveRefImage}
                          className="w-6 h-6 flex items-center justify-center bg-white/90 hover:bg-red-50 text-stone-500 hover:text-red-500 rounded-lg border border-stone-200 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                        How should AI use this image?
                      </label>
                      <input
                        type="text"
                        value={refImagePrompt}
                        onChange={(e) => setRefImagePrompt(e.target.value)}
                        placeholder='e.g. "Recreate this exact can of beer as the hero of the image, keep the label visible"'
                        maxLength={300}
                        className="w-full text-sm px-3 py-2.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
                      />
                      <p className="text-xs text-stone-400 mt-1">Tell the AI exactly how to incorporate your reference into the scene.</p>
                    </div>
                  </div>
                )}

                <input
                  ref={refFileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleRefImageUpload}
                />
              </div>

              {/* Error */}
              {editError && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-lg">
                  <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
                  <span className="leading-snug">{editError}</span>
                  <button onClick={() => setEditError(null)} className="ml-auto shrink-0 text-red-400 hover:text-red-600 cursor-pointer">
                    <i className="ri-close-line" />
                  </button>
                </div>
              )}

              {/* Non-blocking provider fallback notice */}
              {editFallbackMsg && (
                <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs px-3 py-2.5 rounded-lg">
                  <i className="ri-information-line text-sm shrink-0 text-amber-500" />
                  <span className="flex-1 leading-snug">{editFallbackMsg}</span>
                  <button onClick={() => setEditFallbackMsg(null)} className="shrink-0 text-amber-400 hover:text-amber-600 cursor-pointer">
                    <i className="ri-close-line" />
                  </button>
                </div>
              )}

              {/* Edit Quality Selector */}
              <div>
                <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
                  Edit Quality
                </label>
                <div className="flex flex-col gap-1.5">
                  {EDIT_MODES.map((mode) => {
                    const isSelected = editMode === mode.id;
                    const affordable = hasEnoughCredits(mode.credits);
                    return (
                      <button
                        key={mode.id}
                        onClick={() => setEditMode(mode.id)}
                        className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                          isSelected
                            ? 'border-orange-400 bg-orange-50'
                            : 'border-stone-200 bg-stone-50 hover:border-stone-300 hover:bg-white'
                        }`}
                      >
                        <div className={`shrink-0 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-orange-500 bg-orange-500' : 'border-stone-300'
                        }`}>
                          {isSelected && <div className="w-1 h-1 rounded-full bg-white" />}
                        </div>
                        <div className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
                          isSelected ? 'bg-orange-500 text-white' : 'bg-stone-200 text-stone-500'
                        }`}>
                          <i className={`${mode.icon} text-xs`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className={`text-xs font-bold ${isSelected ? 'text-orange-800' : 'text-stone-700'}`}>
                            {mode.label}
                          </span>
                          <p className={`text-xs leading-tight mt-0.5 ${isSelected ? 'text-orange-500' : 'text-stone-400'}`}>
                            {mode.description}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <span className={`text-sm font-extrabold ${isSelected ? 'text-orange-600' : affordable ? 'text-stone-700' : 'text-red-400'}`}>
                            {mode.credits}
                          </span>
                          <span className={`text-xs ml-0.5 ${isSelected ? 'text-orange-400' : 'text-stone-400'}`}>cr</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-1.5 px-0.5">
                  <p className="text-xs text-stone-400">
                    {getEditModeConfig(editMode).recommendedUse}
                  </p>
                  <span className={`text-xs font-semibold ${hasEnoughCredits(getEditModeConfig(editMode).credits) ? 'text-stone-500' : 'text-red-500'}`}>
                    Balance: {creditsRemaining} cr
                  </span>
                </div>
              </div>

              {/* Apply button */}
              <button
                onClick={handleApplyEdit}
                disabled={!editPrompt.trim() || isApplyingEdit || !hasEnoughCredits(getEditModeConfig(editMode).credits)}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap ${
                  editPrompt.trim() && !isApplyingEdit && hasEnoughCredits(getEditModeConfig(editMode).credits)
                    ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                    : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                }`}
              >
                {isApplyingEdit ? (
                  <><i className="ri-loader-4-line animate-spin" /> Applying edit — ~20 seconds...</>
                ) : (
                  <><i className="ri-magic-line" /> Apply {getEditModeConfig(editMode).label} — {getEditModeConfig(editMode).credits} Credits</>
                )}
              </button>

              {/* Edit history for this image */}
              {currentHistory.length > 0 && (
                <div className="space-y-2 pt-1 border-t border-stone-100">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-stone-500 uppercase tracking-widest">Edit History</p>
                    <button
                      onClick={handleRevertToOriginal}
                      className="text-xs text-stone-400 hover:text-orange-500 transition-colors cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-history-line text-xs" /> Revert to original
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {currentHistory.map((entry, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-lg px-3 py-2"
                      >
                        <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0 bg-stone-200">
                          <img src={entry.url} alt={`Edit ${i + 1}`} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-stone-600 truncate italic">&ldquo;{entry.prompt}&rdquo;</p>
                          <p className="text-xs text-stone-400">Edit {i + 1}</p>
                        </div>
                        <button
                          onClick={() => handleRevertToVersion(entry.url)}
                          className="shrink-0 text-xs text-stone-400 hover:text-orange-500 transition-colors cursor-pointer px-2 py-1 rounded-lg hover:bg-orange-50 border border-transparent hover:border-orange-100 whitespace-nowrap"
                        >
                          Use this
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Animation Effects Panel ───────────────────────────────────────── */}
      {imgLoaded && (
        <div className="rounded-xl border border-stone-200 overflow-hidden">
          <button
            onClick={() => setShowAnimPanel((o) => !o)}
            className={`w-full flex items-center gap-3 transition-colors cursor-pointer ${
              showAnimPanel ? 'bg-orange-50 border-b border-orange-100' : 'bg-stone-50 hover:bg-stone-100'
            }`}
          >
            <div className={`w-7 h-7 flex items-center justify-center rounded-lg border shrink-0 transition-colors ${
              showAnimPanel ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-stone-200 text-stone-500'
            }`}>
              <i className="ri-movie-line text-sm" />
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-stone-800">Animation Effects</span>
                {selectedAnimation !== 'none' && (
                  <span className="text-xs text-orange-600 font-semibold bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded-full">Active</span>
                )}
                <span className="text-xs text-stone-400 font-medium bg-stone-100 px-1.5 py-0.5 rounded-full ml-auto mr-6">Preview only</span>
              </div>
              <p className="text-xs text-stone-400 mt-0.5">Add a motion effect to your ad preview</p>
            </div>
            <i className={`${showAnimPanel ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-stone-400 text-lg shrink-0`} />
          </button>

          {showAnimPanel && (
            <div className="px-4 py-4 bg-white space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {ANIMATION_OPTIONS.map((opt) => {
                  const isActive = selectedAnimation === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setSelectedAnimation(opt.id)}
                      className={`flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                        isActive
                          ? 'border-orange-400 bg-orange-50'
                          : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50'
                      }`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                        isActive ? 'bg-orange-500 text-white' : 'bg-stone-100 text-stone-500'
                      }`}>
                        <i className={`${opt.icon} text-base`} />
                      </div>
                      <span className={`text-xs font-bold leading-tight text-center ${isActive ? 'text-orange-700' : 'text-stone-700'}`}>
                        {opt.label}
                      </span>
                      <span className="text-xs text-stone-400 text-center leading-tight">{opt.desc}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-stone-400 flex items-center gap-1.5">
                <i className="ri-information-line text-stone-300" />
                Animation is a live preview effect — it doesn&apos;t affect your downloaded image file.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Animate This Ad — Video Ad Feature ───────────────────────── */}
      {imgLoaded && activeSrc && (
        <>
          <VideoAdModal
            isOpen={videoAdOpen}
            onClose={() => setVideoAdOpen(false)}
            sourceImageUrl={activeSrc}
            creditsRemaining={creditsRemaining}
            hasEnoughCredits={hasEnoughCredits}
            onCreditsRefetch={refetchCredits}
            generationId={result.generationId}
          />
          <button
            onClick={() => setVideoAdOpen(true)}
            className="w-full group relative overflow-hidden flex items-center gap-3.5 px-5 py-4 rounded-xl border-2 border-rose-200 bg-gradient-to-r from-rose-50 to-orange-50 hover:from-rose-100 hover:to-orange-100 hover:border-rose-300 transition-all duration-200 cursor-pointer"
          >
            {/* Shimmer */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none overflow-hidden rounded-xl">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-700" />
            </div>
            <div className="shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-orange-400 text-white">
              <i className="ri-film-fill text-lg" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-extrabold text-stone-900">Animate This Ad</span>
                <span className="text-xs font-bold bg-gradient-to-r from-rose-500 to-orange-400 text-white px-1.5 py-0.5 rounded-full whitespace-nowrap">
                  New
                </span>
              </div>
              <p className="text-xs text-stone-500 mt-0.5 leading-snug">
                Turn this image into a short video ad — {VIDEO_AD_GENERATE_CREDITS} credits · 5 sec
              </p>
            </div>
            <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-rose-200 text-rose-500 group-hover:bg-rose-500 group-hover:text-white group-hover:border-rose-500 transition-all duration-200">
              <i className="ri-arrow-right-line text-sm" />
            </div>
          </button>
        </>
      )}

      {/* Overlay text controls */}
      {imgLoaded && showOverlay && (
        <div className="bg-stone-50 border border-stone-100 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-stone-600 uppercase tracking-widest">Overlay Text</p>
            <div className="flex items-center bg-stone-200 rounded-full p-0.5 gap-0.5">
              <button
                onClick={() => setOverlayMode('ai')}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  overlayMode === 'ai' ? 'bg-white text-stone-800' : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                AI Overlay
              </button>
              <button
                onClick={() => setOverlayMode('custom')}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  overlayMode === 'custom' ? 'bg-white text-stone-800' : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                Custom
              </button>
            </div>
          </div>

          {overlayMode === 'ai' && aiOverlay && (
            <div className="space-y-1.5">
              <div className="flex items-start gap-2 text-xs">
                <span className="shrink-0 font-semibold text-stone-500 w-20 pt-0.5">Headline</span>
                <span className="text-stone-700 leading-snug">{aiOverlay.headline}</span>
              </div>
              <div className="flex items-start gap-2 text-xs">
                <span className="shrink-0 font-semibold text-stone-500 w-20 pt-0.5">Subheadline</span>
                <span className="text-stone-700 leading-snug">{aiOverlay.subheadline}</span>
              </div>
              <div className="flex items-start gap-2 text-xs">
                <span className="shrink-0 font-semibold text-stone-500 w-20 pt-0.5">CTA</span>
                <span className="inline-block bg-orange-100 text-orange-700 font-semibold px-2 py-0.5 rounded-md">{aiOverlay.cta}</span>
              </div>
            </div>
          )}

          {overlayMode === 'custom' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-xs font-bold text-stone-500 uppercase tracking-widest">Main Headline</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setCustomTextType('headline'); setCustomTextIdx(0); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                      customTextType === 'headline'
                        ? 'bg-stone-700 border-stone-700 text-white'
                        : 'bg-white border-stone-200 text-stone-600 hover:border-orange-300'
                    }`}
                  >
                    Headline
                  </button>
                  <button
                    onClick={() => { setCustomTextType('hook'); setCustomTextIdx(0); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                      customTextType === 'hook'
                        ? 'bg-stone-700 border-stone-700 text-white'
                        : 'bg-white border-stone-200 text-stone-600 hover:border-orange-300'
                    }`}
                  >
                    Hook
                  </button>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {customTexts.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCustomTextIdx(idx)}
                      className={`w-7 h-7 flex items-center justify-center rounded-md text-xs font-bold border transition-all cursor-pointer ${
                        customTextIdx === idx
                          ? 'bg-stone-800 border-stone-800 text-white'
                          : 'bg-white border-stone-200 text-stone-500 hover:border-stone-400'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
                {customText && (
                  <p className="text-xs text-stone-500 leading-snug bg-white border border-stone-100 rounded-lg px-3 py-2 italic">
                    {customText}
                  </p>
                )}
              </div>

              {result.subheadlines && result.subheadlines.length > 0 && (
                <div className="space-y-2 pt-1 border-t border-stone-100">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-stone-500 uppercase tracking-widest">Sub Headline</p>
                    {customSubheadlineIdx >= 0 && (
                      <span className="text-xs text-teal-600 font-semibold bg-teal-50 border border-teal-100 px-2 py-0.5 rounded-full">
                        Variation {customSubheadlineIdx + 1} active
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <button
                      onClick={() => setCustomSubheadlineIdx(-1)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                        customSubheadlineIdx === -1
                          ? 'bg-stone-700 border-stone-700 text-white'
                          : 'bg-white border-stone-200 text-stone-500 hover:border-stone-400'
                      }`}
                    >
                      Off
                    </button>
                    {result.subheadlines.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCustomSubheadlineIdx(idx)}
                        className={`w-7 h-7 flex items-center justify-center rounded-md text-xs font-bold border transition-all cursor-pointer ${
                          customSubheadlineIdx === idx
                            ? 'bg-teal-600 border-teal-600 text-white'
                            : 'bg-white border-stone-200 text-stone-500 hover:border-teal-300'
                        }`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </div>
                  {customSubheadlineIdx >= 0 && result.subheadlines[customSubheadlineIdx] && (
                    <p className="text-xs text-stone-500 leading-snug bg-white border border-teal-100 rounded-lg px-3 py-2 italic">
                      {result.subheadlines[customSubheadlineIdx]}
                    </p>
                  )}
                </div>
              )}

              {result.ctas.length > 0 && (
                <div className="space-y-2 pt-1 border-t border-stone-100">
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-widest">CTA Button</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {result.ctas.map((cta, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCustomCtaIdx(idx)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                          customCtaIdx === idx
                            ? 'bg-stone-700 border-stone-700 text-white'
                            : 'bg-white border-stone-200 text-stone-600 hover:border-orange-300'
                        }`}
                      >
                        {cta}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Open in Editor + Download + Copy HTML */}
      {imgLoaded && (
        <div className="space-y-2">
          {/* Editor CTA — primary action */}
          <button
            onClick={handleOpenEditor}
            className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold text-stone-600 bg-stone-50 hover:bg-stone-100 border border-stone-200 hover:border-stone-300 px-4 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-2-fill" />
            Open in Visual Editor
            <span className="text-xs text-stone-400">(move · edit · style)</span>
          </button>

          {/* Export error */}
          {captureError && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-lg">
              <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
              <span className="flex-1 leading-snug">{captureError}</span>
              <button onClick={() => setCaptureError(null)} className="shrink-0 text-red-400 hover:text-red-600 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleDownloadWithOverlays}
              disabled={isCapturingOverlay}
              className={`flex-1 flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl border transition-all duration-200 whitespace-nowrap ${
                isCapturingOverlay
                  ? 'bg-stone-100 border-stone-200 text-stone-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-500 to-amber-400 text-white border-transparent hover:from-orange-600 hover:to-amber-500 cursor-pointer'
              }`}
            >
              {isCapturingOverlay ? (
                <><i className="ri-loader-4-line animate-spin" /> Exporting...</>
              ) : (
                <><i className="ri-image-2-line" /> Export PNG with Overlays</>
              )}
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center justify-center gap-2 text-sm font-semibold text-stone-600 bg-stone-50 hover:bg-stone-100 border border-stone-200 hover:border-stone-300 px-4 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
              title="Download clean background image only"
            >
              <i className="ri-image-line" />
              Clean
              {hasLocalEdit && <span className="text-xs text-orange-500 font-semibold">(edited)</span>}
            </button>
            <button
              onClick={handleCopyHtml}
              title="Copy as embeddable HTML with animation"
              className={`flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                copiedHtml
                  ? 'bg-green-500 border-green-500 text-white'
                  : 'text-stone-600 bg-stone-50 hover:bg-stone-100 border-stone-200 hover:border-stone-300'
              }`}
            >
              <i className={copiedHtml ? 'ri-check-line' : 'ri-code-s-slash-line'} />
              {copiedHtml ? 'Copied!' : 'HTML'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Main ResultsPanel ──────────────────────────────────────────────────── */
export default function ResultsPanel({
  result,
  isGenerating,
  onRegenerate,
  onSave,
  onRegenerateCopy,
  platform,
  businessName,
  adsPerBatch = 1,
  businessContext,
  logoUrl,
  editedUrls,
  editHistory,
  onEditedUrlUpdate,
  onEditHistoryUpdate,
  onRevertToOriginal,
  onRegenerateSingle,
}: ResultsPanelProps) {
  const [saved, setSaved] = useState(false);
  const [isCopyRegenerating, setIsCopyRegenerating] = useState(false);
  const [copyRegenError, setCopyRegenError] = useState<string | null>(null);
  // Local mutable copy of result arrays so individual item regen updates the UI
  const [localResult, setLocalResult] = useState<AdResult | null>(null);

  // Sync localResult when result changes (new generation)
  const effectiveResult = localResult ?? result;

  const handleSave = () => {
    onSave();
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleRegenerateCopy = async () => {
    if (!onRegenerateCopy || isCopyRegenerating) return;
    setIsCopyRegenerating(true);
    setCopyRegenError(null);
    try {
      await onRegenerateCopy();
      // Clear local overrides so fresh result from parent is shown
      setLocalResult(null);
    } catch (err) {
      setCopyRegenError(err instanceof Error ? err.message : 'Copy regeneration failed. Please try again.');
    } finally {
      setIsCopyRegenerating(false);
    }
  };

  const handleItemRegenerated = (copyType: CopyType, index: number, newText: string) => {
    const base = effectiveResult;
    if (!base) return;
    const updated = { ...base };
    const arrayMap: Record<CopyType, keyof AdResult> = {
      hook: 'hooks',
      headline: 'headlines',
      subheadline: 'subheadlines',
      primaryText: 'primaryText',
      cta: 'ctas',
    };
    const field = arrayMap[copyType];
    const arr = [...((updated[field] as string[]) ?? [])];
    arr[index] = newText;
    (updated[field] as string[]) = arr;
    setLocalResult(updated);
  };

  /* Loading state */
  if (isGenerating) {
    return <GenerationProgressState platform={platform} adsPerBatch={adsPerBatch} />;
  }

  if (isGenerating && !isGenerating) {
    return (
      <div className="bg-white rounded-2xl border border-stone-100 h-full min-h-[500px] flex flex-col items-center justify-center gap-5">
        <div className="relative">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-400">
            <i className="ri-flashlight-fill text-white text-2xl" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border-2 border-white flex items-center justify-center">
            <span className="w-3 h-3 rounded-full bg-orange-400 animate-ping" />
          </div>
        </div>
        <div className="text-center">
          <p className="text-base font-bold text-stone-900 mb-1">Writing your ads...</p>
          <p className="text-sm text-stone-400">Crafting hooks, headlines &amp; visuals for {platform}</p>
        </div>
        <div className="flex gap-1.5 mt-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-orange-400 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
        <p className="text-xs text-stone-400 max-w-xs text-center px-4">
          Generating {adsPerBatch} unique creative image{adsPerBatch !== 1 ? 's' : ''} in parallel — this takes about {adsPerBatch <= 3 ? '30' : '60'} seconds
        </p>
      </div>
    );
  }

  /* Empty state */
  if (!effectiveResult) {
    return (
      <div className="bg-white rounded-2xl border border-stone-100 h-full min-h-[500px] flex flex-col items-center justify-center gap-5 px-8 text-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-orange-50 border border-orange-100">
          <i className="ri-magic-fill text-orange-400 text-3xl" />
        </div>
        <div>
          <p className="text-lg font-bold text-stone-800 mb-2">Your ads will appear here</p>
          <p className="text-sm text-stone-400 max-w-xs leading-relaxed">
            Fill in your business details on the left and click{' '}
            <strong className="text-stone-600">Generate Ads</strong> to get started.
          </p>
        </div>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          {[
            'Hooks that grab attention',
            'Headlines that convert',
            'Ad copy that sells',
            'CTAs that get clicks',
            '3 unique creative image options',
          ].map((item) => (
            <div key={item} className="flex items-center gap-2.5 text-sm text-stone-400">
              <i className="ri-check-line text-orange-400 shrink-0" />
              {item}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activeImagePrompt = effectiveResult.imagePrompts?.[0] ?? effectiveResult.imagePrompt;

  return (
    <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 md:px-6 py-3 md:py-4 border-b border-stone-100 flex items-center justify-between shrink-0 gap-2">
        <div className="min-w-0 shrink">
          <h2 className="text-sm md:text-base font-bold text-stone-900">Generated Ads</h2>
          <p className="text-xs text-stone-400 mt-0.5 truncate">
            {platform} · <strong className="text-stone-600">{businessName}</strong>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onRegenerate}
            className="flex items-center gap-1.5 text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 px-2.5 md:px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            <span className="hidden sm:inline">Regenerate</span>
          </button>
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 md:px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
              saved
                ? 'bg-green-500 text-white'
                : 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500'
            }`}
          >
            <i className={saved ? 'ri-check-line' : 'ri-save-line'} />
            {saved ? 'Saved!' : 'Save'}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto">
        <div className="px-4 md:px-6 py-4 md:py-6 space-y-8">

          {/* ── 1. Creative Preview ─────────────────────────────────── */}
          {effectiveResult.generationWarning && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
              <i className="ri-error-warning-line text-base shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider">Partial creative batch</p>
                <p className="text-sm mt-1 leading-relaxed">{effectiveResult.generationWarning}</p>
              </div>
            </div>
          )}

          <AdCreativePreview
            result={effectiveResult}
            editedUrls={editedUrls}
            editHistory={editHistory}
            onEditedUrlUpdate={onEditedUrlUpdate}
            onEditHistoryUpdate={onEditHistoryUpdate}
            onRevertToOriginal={onRevertToOriginal}
            onRegenerateSingle={onRegenerateSingle}
            logoUrl={logoUrl}
          />

          {/* ── 2. Copy Results ─────────────────────────────────────── */}
          <CopyResultsSection
            result={effectiveResult}
            businessContext={businessContext ?? { businessName, platform }}
            isCopyRegenerating={isCopyRegenerating}
            copyRegenError={copyRegenError}
            onRegenerateCopy={handleRegenerateCopy}
            onClearCopyRegenError={() => setCopyRegenError(null)}
            onItemRegenerated={handleItemRegenerated}
          />

          {/* ── 3. Creative Direction ───────────────────────────────── */}
          {activeImagePrompt && (
            <CreativeDirection
              prompt={activeImagePrompt}
              label="Image prompt used by AI"
            />
          )}
        </div>
      </div>
    </div>
  );
}
